import * as React from 'react';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  HANDLING_UNIT_TEMPERATURES,
  isHandlingUnitTemperature,
  type HandlingUnitType,
} from '@klareospace/pharos-lib-types';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@klareospace/pharos-lib-web-utils';

import type { ContentHandlingUnitLineFormInput } from '../../schemas/order-creation.schema';

// ─── Constants ────────────────────────────────────────────────────────────────

const HANDLING_UNIT_TEMPERATURE_OPTIONS = Object.values(HANDLING_UNIT_TEMPERATURES);

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HandlingUnitLineCardProps {
  handlingUnitType: HandlingUnitType | undefined;
  line: ContentHandlingUnitLineFormInput;
  onChange: (patch: Partial<ContentHandlingUnitLineFormInput>) => void;
  onRemove: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HandlingUnitLineCard({
  handlingUnitType,
  line,
  onChange,
  onRemove,
}: HandlingUnitLineCardProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('order-creation');

  function handleQuantityChange(nextQuantity: number): void {
    if (nextQuantity < 1) return;
    onChange({ quantity: nextQuantity });
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-background p-2.5 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate text-sm font-medium text-foreground">{handlingUnitType?.label ?? line.typeCode}</p>
        {handlingUnitType && (
          <p className="truncate text-xs text-muted-foreground">
            {handlingUnitType.code} · {handlingUnitType.weight.value} {tCommon(`units.${handlingUnitType.weight.unit}`)}{' '}
            · {handlingUnitType.volume.value} {tCommon(`units.${handlingUnitType.volume.unit}`)}
          </p>
        )}
        <Input
          aria-label={t('sections.content.fields.handlingUnits.barcodeAriaLabel')}
          className="h-7 font-mono text-xs uppercase"
          onChange={(event): void => onChange({ barcode: event.target.value.toUpperCase() })}
          onKeyDown={(event): void => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            event.currentTarget.blur();
          }}
          placeholder={t('sections.content.fields.handlingUnits.barcodePlaceholder')}
          value={line.barcode}
        />
      </div>
      <div className="flex items-center gap-1 self-end sm:shrink-0 sm:self-center">
        <Select
          onValueChange={(value): void => {
            if (!isHandlingUnitTemperature(value)) return;
            onChange({ temperature: value });
          }}
          value={line.temperature}
        >
          <SelectTrigger
            aria-label={t('sections.content.fields.handlingUnits.temperatureLabel')}
            className="h-7 w-[92px] px-2 text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HANDLING_UNIT_TEMPERATURE_OPTIONS.map((temperature) => (
              <SelectItem key={temperature} value={temperature}>
                {t(`sections.content.fields.handlingUnits.temperature.${temperature}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          aria-label={t('sections.content.fields.handlingUnits.decreaseQuantity')}
          className="h-7 w-7"
          disabled={line.quantity <= 1}
          onClick={(): void => handleQuantityChange(line.quantity - 1)}
          size="icon"
          type="button"
          variant="outline"
        >
          <Minus aria-hidden="true" className="h-3 w-3" />
        </Button>
        <Input
          aria-label={t('sections.content.fields.handlingUnits.quantityAriaLabel')}
          className="h-7 w-12 px-1 text-center text-sm"
          min={1}
          onChange={(event): void => {
            const parsed = parseInt(event.target.value, 10);
            if (!Number.isNaN(parsed)) handleQuantityChange(parsed);
          }}
          type="number"
          value={line.quantity}
        />
        <Button
          aria-label={t('sections.content.fields.handlingUnits.increaseQuantity')}
          className="h-7 w-7"
          onClick={(): void => handleQuantityChange(line.quantity + 1)}
          size="icon"
          type="button"
          variant="outline"
        >
          <Plus aria-hidden="true" className="h-3 w-3" />
        </Button>
        <Button
          aria-label={t('sections.content.fields.handlingUnits.remove')}
          className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={onRemove}
          size="icon"
          type="button"
          variant="ghost"
        >
          <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
